import { isSameLocalDay } from '@/lib/dateKey';
import { DAY_SHORT } from '@/lib/dateNames';

/** Midnight of the given date's local day. */
export function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** The given date with its clock set to `minutesIntoDay` (local). */
export function withTime(date: Date, minutesIntoDay: number): Date {
  const d = new Date(date);
  d.setHours(Math.floor(minutesIntoDay / 60), minutesIntoDay % 60, 0, 0);
  return d;
}

/** Same clock time, one calendar day later (local). */
export function nextDay(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return d;
}

/**
 * Format an "HH:MM" 24-hour clock string as human time — "9 AM", "9:30 AM",
 * "12 PM". Brainday voice: time is human, not military. Shared by the habit
 * rows / detail sheet / timeline so 24h values never leak into the UI.
 */
export const formatTime = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${hh12} ${ampm}` : `${hh12}:${String(m).padStart(2, '0')} ${ampm}`;
};

/**
 * Human time from a Date — the Date-accepting entry point for the timeline /
 * capture / calendar surfaces that previously hand-rolled this. Delegates to
 * formatTime so the "9 AM" / "9:30 AM" voice stays in one place.
 */
export const formatTimeFromDate = (d: Date): string =>
  formatTime(`${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`);

/**
 * Human label for a whole-hour tick (0–23) — "12 AM", "9 AM", "12 PM", "9 PM".
 * Used by the timeline hour grid and the onboarding energy chart.
 */
export const formatHourLabel = (hour: number): string =>
  hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`;

/**
 * Canonical time range — "9 AM – 10:30 AM" (space en-dash space, per the
 * voice rules). Now cards, up-next rows and the calendar column all build
 * ranges through here.
 */
export const formatTimeRange = (start: Date, end: Date): string =>
  `${formatTimeFromDate(start)} – ${formatTimeFromDate(end)}`;

export interface RelativeTime {
  /** compact: "now", "in 8m", "1h 42m ago" · long: "just now", "5 min ago", "2 hr ago" */
  label: string;
  past: boolean;
}

/**
 * Relative time in two house styles. `compact` is the Today-surface column
 * style ("in 8m", "5m ago"); `long` is the settings/calendar prose style
 * ("just now", "5 min ago", "2 hr ago", "3 days ago"). Three inconsistent
 * hand-rolled versions of this existed before — add styles here rather than
 * rolling a fourth.
 */
export function formatRelative(
  target: Date,
  now: Date,
  style: 'compact' | 'long' = 'compact',
): RelativeTime {
  const diffMs = target.getTime() - now.getTime();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);

  if (style === 'long') {
    if (abs < 60_000) return { label: 'just now', past };
    const span =
      abs < 3_600_000
        ? `${Math.floor(abs / 60_000)} min`
        : abs < 86_400_000
          ? `${Math.floor(abs / 3_600_000)} hr`
          : `${Math.floor(abs / 86_400_000)} days`;
    return { label: past ? `${span} ago` : `in ${span}`, past };
  }

  const diffMin = Math.round(diffMs / 60_000);
  if (Math.abs(diffMin) < 1) return { label: 'now', past: false };
  const absMin = Math.abs(diffMin);
  const hours = Math.floor(absMin / 60);
  const mins = absMin % 60;
  const span = hours === 0 ? `${mins}m` : mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  return { label: diffMin < 0 ? `${span} ago` : `in ${span}`, past: diffMin < 0 };
}

/**
 * Voice-formatted schedule confirmation — "today 3 PM", "tomorrow 9:30 AM",
 * "Wed 9 at 2 PM". Shared by the capture and reschedule toasts (previously
 * duplicated near-verbatim in useCapture and SchedulePopover).
 */
export function formatScheduledFor(date: Date): string {
  // Defensive: an Invalid Date would otherwise render raw "NaN"/"undefined" into
  // a toast (voice rule: garbage strings never reach users). Callers guard their
  // inputs, so this only ever fires as a last-resort fallback.
  if (Number.isNaN(date.getTime())) return 'later';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameLocalDay(date, today)) return `today ${formatTimeFromDate(date)}`;
  if (isSameLocalDay(date, tomorrow)) return `tomorrow ${formatTimeFromDate(date)}`;
  return `${DAY_SHORT[date.getDay()]} ${date.getDate()} at ${formatTimeFromDate(date)}`;
}
